import { FormikProvider, useFormik } from 'formik'
import { Autocomplete, CircularProgress, Container, TextField } from '@mui/material'
import * as yup from 'yup'
import Dialog from 'components/dialog/dialog'
import Notification from 'components/notification'
import GLOBAL from 'modules/global'
import type { PayloadTokenCategoryProps } from 'modules/partial'
import type { DetailStateProps, ErrorProps } from 'modules/types'
import GlobalStyle from 'modules/styles'
import { useCreateTokenCategoryMutation, useGetTokenPartnerDropdownMutation } from 'store/token'
import DialogStyle from './style'

const Create = ({ open, onClose }: DetailStateProps) => {
  const [createTokenCategory, create] = useCreateTokenCategoryMutation()
  const [getTokenPartnerDropdown, dropdownPartner] = useGetTokenPartnerDropdownMutation()

  const scheme = yup.object<PayloadTokenCategoryProps>({
    nameEn: yup.string().required('Token Category Name English is required'),
    companyId: yup.string().required('Partner Category  is required'),
    monetaryValue: yup.number().required('Monetary Value  is required')
  })

  const formik = useFormik<PayloadTokenCategoryProps>({
    validationSchema: scheme,
    enableReinitialize: true,
    validateOnMount: true,
    initialValues: {
      companyId: '',
      nameEn: '',
      monetaryValue: 0
    },
    onSubmit: (values: PayloadTokenCategoryProps) => handleSubmit(values)
  })

  const handleSubmit = (e: PayloadTokenCategoryProps) => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const payload: any = {
      companyId: e.companyId,
      nameEn: e.nameEn,
      monetaryValue: e.monetaryValue
    }

    createTokenCategory(payload).then()
  }

  return (
    <>
      <Dialog
        open={open}
        title='Add Token Category'
        onCancel={onClose}
        onSubmit={() => formik.handleSubmit()}
        loading={create.isLoading}
        isDisabled={!formik.isValid}
      >
        <Container {...DialogStyle.Container}>
          <FormikProvider value={formik}>
            <TextField
              id='nameEn'
              variant='outlined'
              label='Token Category Name'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched && formik.touched.nameEn && Boolean(formik.errors.nameEn)}
              helperText={
                formik.touched && formik.touched.nameEn && formik.errors && formik.errors.nameEn
              }
              fullWidth
            />

            <Autocomplete
              options={dropdownPartner.data || []}
              getOptionLabel={(list) => list.name}
              isOptionEqualToValue={(option, value) =>
                option && value ? option.id == value.id : false
              }
              onOpen={() => getTokenPartnerDropdown()}
              onChange={(_, id) => formik.setFieldValue('companyId', id && id.id)}
              ListboxProps={GlobalStyle.ListBox}
              renderOption={(props, item) => (
                <li {...props} key={item.id}>
                  {item.name}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  name='companyId'
                  label='Select A Partner'
                  error={
                    formik.touched && formik.touched.companyId && Boolean(formik.errors.companyId)
                  }
                  helperText={
                    formik.touched &&
                    formik.touched.companyId &&
                    formik.errors &&
                    formik.errors.companyId
                  }
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {dropdownPartner.isLoading && (
                          <CircularProgress color='inherit' size={20} />
                        )}
                        {params.InputProps.endAdornment}
                      </>
                    )
                  }}
                />
              )}
            />

            <TextField
              id='monetaryValue'
              variant='outlined'
              label='Monetary Value'
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={
                formik.touched &&
                formik.touched.monetaryValue &&
                Boolean(formik.errors.monetaryValue)
              }
              helperText={
                formik.touched &&
                formik.touched.monetaryValue &&
                formik.errors &&
                formik.errors.monetaryValue
              }
              fullWidth
            />
          </FormikProvider>
        </Container>
      </Dialog>

      <Notification
        open={!create.isLoading && !create.isUninitialized}
        onClose={() => (create.isError ? create.reset() : location.reload())}
        isError={Boolean(create.error) && create.isError}
        message={GLOBAL.returnExceptionMessage(create.isError, create.error as ErrorProps)}
      />
    </>
  )
}

export default Create
